import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Loading from '../../components/common/Loading';
import { useForm } from 'react-hook-form';
import { formatDateShort } from '../../utils/formatDate';

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll({ search, limit: 100 });
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    reset();
    setModalOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    reset(user);
    setModalOpen(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer l'utilisateur ${user.fullName}?`)) return;

    try {
      await usersAPI.delete(user._id || user.id);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingUser) {
        await usersAPI.update(editingUser._id || editingUser.id, data);
        toast.success('Utilisateur modifié');
      } else {
        // Lors de la création, forcer le statut à 'active'
        const userData = {
          ...data,
          status: 'active' // Toujours actif lors de la création
        };
        await usersAPI.create(userData);
        toast.success('Utilisateur créé');
      }
      setModalOpen(false);
      fetchUsers();
    } catch (error) {
      // Afficher les erreurs de validation détaillées
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg || 
                          error.response?.data?.error ||
                          'Erreur lors de la création';
      toast.error(errorMessage);
      console.error('Erreur détaillée:', error.response?.data);
    }
  };

  const columns = [
    { header: 'Nom', accessor: 'fullName' },
    { header: 'Email', accessor: 'email' },
    {
      header: 'Rôle',
      accessor: 'role',
      render: (user) => (
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          background: user.role === 'admin' ? '#6366f1' : '#8b5cf6',
          color: 'white',
        }}>
          {user.role}
        </span>
      ),
    },
    {
      header: 'Statut',
      accessor: 'status',
      render: (user) => (
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          background: user.status === 'active' ? '#10b981' : '#ef4444',
          color: 'white',
        }}>
          {user.status}
        </span>
      ),
    },
    {
      header: 'Créé le',
      accessor: 'createdAt',
      render: (user) => formatDateShort(user.createdAt),
    },
  ];

  if (loading) return <Loading />;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Utilisateurs</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          ➕ Ajouter un utilisateur
        </button>
      </div>

      <div style={styles.searchBar}>
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <Table
        columns={columns}
        data={users}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
      >
        <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
          <div style={styles.formGroup}>
            <label>Nom complet</label>
            <input
              {...register('fullName', { required: true })}
              className="input"
            />
          </div>
          <div style={styles.formGroup}>
            <label>Email</label>
            <input
              type="email"
              {...register('email', { required: true })}
              className="input"
            />
          </div>
          {!editingUser && (
            <div style={styles.formGroup}>
              <label>Mot de passe</label>
              <input
                type="password"
                {...register('password', { required: !editingUser })}
                className="input"
              />
            </div>
          )}
          <div style={styles.formGroup}>
            <label>Rôle</label>
            <select {...register('role')} className="input" defaultValue="user">
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {editingUser && (
            <div style={styles.formGroup}>
              <label>Statut</label>
              <select {...register('status')} className="input">
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
              <small style={styles.helpText}>
                Les utilisateurs inactifs ne peuvent pas se connecter
              </small>
            </div>
          )}
          {!editingUser && (
            <div style={styles.infoBox}>
              <p style={styles.infoText}>
                ℹ️ Les nouveaux utilisateurs seront créés avec le statut <strong>Actif</strong> et pourront se connecter immédiatement.
              </p>
            </div>
          )}
          <div style={styles.formActions}>
            <button type="button" onClick={() => setModalOpen(false)} className="btn btn-outline">
              Annuler
            </button>
            <button type="submit" className="btn btn-primary">
              {editingUser ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--text)',
  },
  searchBar: {
    marginBottom: '1.5rem',
  },
  searchInput: {
    width: '100%',
    maxWidth: '400px',
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  formActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  helpText: {
    fontSize: '0.75rem',
    color: 'var(--text-light)',
    marginTop: '0.25rem',
  },
  infoBox: {
    background: 'var(--light)',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--border)',
  },
  infoText: {
    fontSize: '0.875rem',
    color: 'var(--text)',
    margin: 0,
  },
};

export default UsersList;








